import { FormEvent, useState, useEffect } from "react";
import { Turnstile } from "@marsidev/react-turnstile";
import { TURNSTILE_SITE_KEY } from "../../app/config";
import { LoadingDots } from "../../shared/components/LoadingDots";
import { useLanguage } from "../../i18n";

const EXAMPLE_PLAYLIST_ID = "7flKSbtA0xvAQLWexoxOpw";

type PlaylistFormProps = {
  isSubmitting: boolean;
  onSubmit: (payload: { playlistRef: string; turnstileToken?: string }) => void;
  onReset: () => void;
  initialValue?: string | null;
};

export const PlaylistForm = ({
  isSubmitting,
  onSubmit,
  onReset,
  initialValue
}: PlaylistFormProps): JSX.Element => {
  const { t } = useLanguage();
  const [playlistRef, setPlaylistRef] = useState(initialValue ?? "");
  
  // Sync with initialValue when it changes (e.g., from shared link)
  useEffect(() => {
    if (initialValue) setPlaylistRef(initialValue);
  }, [initialValue]);
  const [turnstileToken, setTurnstileToken] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(null);

  const requiresTurnstile = Boolean(TURNSTILE_SITE_KEY);
  const playlistValue = playlistRef.trim();
  const canSubmit =
    Boolean(playlistValue) && (!requiresTurnstile || Boolean(turnstileToken));

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    if (!playlistValue) {
      setError(t.form.errors.emptyPlaylist);
      return;
    }

    if (requiresTurnstile && !turnstileToken) {
      setError(t.form.errors.cloudflareCheck);
      return;
    }

    onSubmit({ playlistRef: playlistValue, turnstileToken });
  };

  const handleReset = () => {
    setPlaylistRef("");
    setTurnstileToken(undefined);
    setError(null);
    onReset();
  };

  return (
    <form onSubmit={handleSubmit} aria-label="Playlist submission form" className="playlist-form">
      <div className="input-wrapper">
        <input
          className="input"
          name="playlist"
          placeholder={t.form.placeholder}
          value={playlistRef}
          onChange={(event) => setPlaylistRef(event.target.value)}
          disabled={isSubmitting}
          aria-label="Playlist URL or ID"
        />
        <button
          type="submit"
          className="action-btn"
          disabled={isSubmitting || !canSubmit}
        >
          {isSubmitting ? (
            <div className="loading-dots">
              <span />
              <span />
              <span />
            </div>
          ) : (
            t.form.go
          )}
        </button>
      </div>

      {!playlistValue && !isSubmitting && (
        <button
          type="button"
          className="try-example-btn"
          onClick={() => {
            setPlaylistRef(EXAMPLE_PLAYLIST_ID);
            onSubmit({ playlistRef: EXAMPLE_PLAYLIST_ID, turnstileToken });
          }}
        >
          {t.form.tryExample} →
        </button>
      )}

      {requiresTurnstile && (
        <div style={{ display: "none" }}>
          <Turnstile
            siteKey={TURNSTILE_SITE_KEY}
            options={{ theme: "light", action: "submit" }}
            onSuccess={(token: string) => {
              setTurnstileToken(token);
              setError(null);
            }}
            onExpire={() => setTurnstileToken(undefined)}
            onError={() => setError(t.form.errors.cloudflareValidation)}
          />
        </div>
      )}

      {error && <div className="error-banner">{error}</div>}
    </form>
  );
};

